import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import rewardsTheme from '../../../theme/rewardsTheme';
import Text from '../Text';

import Otp from './Otp';

const meta = {
  title: 'Components/Primitives/OTP',
  component: Otp,
  argTypes: {
    numInputs: {
      control: 'number',
      min: 4,
      max: 8,
    },
    error: {
      control: 'boolean',
    },
  },
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <View style={styles.container}>
          <Story />
        </View>
      </ThemeProvider>
    ),
  ],
} satisfies Meta<typeof Otp>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {
  render: () => {
    const [code, setCode] = useState('');
    return (
      <View style={styles.variantsContainer}>
        <Text variant="heading3">Enter OTP Code</Text>
        <Text variant="body2">Code entered: {code || '(none)'}</Text>
        <Otp numInputs={6} onChangeText={setCode} />
      </View>
    );
  },
};

export const SixDigits: Story = {
  render: () => {
    const [code, setCode] = useState('');
    return (
      <View style={styles.variantsContainer}>
        <Text variant="body2">6-digit code</Text>
        <Otp numInputs={6} onChangeText={setCode} />
        <Text variant="caption">Entered: {code}</Text>
      </View>
    );
  },
};

export const FourDigits: Story = {
  render: () => {
    const [code, setCode] = useState('');
    return (
      <View style={styles.variantsContainer}>
        <Text variant="body2">4-digit code</Text>
        <Otp numInputs={4} onChangeText={setCode} />
        <Text variant="caption">Entered: {code}</Text>
      </View>
    );
  },
};

export const WithError: Story = {
  render: () => {
    const [code, setCode] = useState('');
    const [showError, setShowError] = useState(false);

    const handleChange = (text: string) => {
      setCode(text);
      if (text.length === 6 && text !== '123456') {
        setShowError(true);
      } else {
        setShowError(false);
      }
    };

    return (
      <View style={styles.variantsContainer}>
        <Text variant="body2">Enter code (try entering 123456)</Text>
        <Otp numInputs={6} onChangeText={handleChange} error={showError} />
        {showError ? (
          <Text variant="caption" style={{color: '#C81E1E'}}>
            Invalid code. Please try again.
          </Text>
        ) : null}
        {code === '123456' && (
          <Text variant="caption" style={{color: '#28A745'}}>
            Code verified successfully!
          </Text>
        )}
      </View>
    );
  },
};

export const ResetValue: Story = {
  render: () => {
    const [code, setCode] = useState('');
    const [resetKey, setResetKey] = useState('');

    const handleReset = () => {
      setCode('');
      setResetKey('');
    };

    return (
      <View style={styles.variantsContainer}>
        <Text variant="body2">OTP with reset functionality</Text>
        <Otp numInputs={6} onChangeText={setCode} value={resetKey} />
        <Text variant="caption">Entered: {code || '(empty)'}</Text>
        <View style={{marginTop: 16}}>
          <Text
            variant="body2"
            style={{color: '#007AFF', textAlign: 'center'}}
            onPress={handleReset}>
            Clear OTP
          </Text>
        </View>
      </View>
    );
  },
};

export const AllVariants: Story = {
  render: () => {
    const [code6, setCode6] = useState('');
    const [code4, setCode4] = useState('');
    const [codeError, setCodeError] = useState('');

    return (
      <View style={styles.variantsContainer}>
        <View>
          <Text variant="heading4">6-digit OTP</Text>
          <Otp numInputs={6} onChangeText={setCode6} />
          <Text variant="caption">Code: {code6}</Text>
        </View>

        <View style={{marginTop: 24}}>
          <Text variant="heading4">4-digit OTP</Text>
          <Otp numInputs={4} onChangeText={setCode4} />
          <Text variant="caption">Code: {code4}</Text>
        </View>

        <View style={{marginTop: 24}}>
          <Text variant="heading4">OTP with error state</Text>
          <Otp numInputs={6} onChangeText={setCodeError} error={true} />
          <Text variant="caption" style={{color: '#C81E1E'}}>
            Invalid code entered ({codeError || 'none'})
          </Text>
        </View>
      </View>
    );
  },
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  variantsContainer: {
    gap: 8,
  },
});
